import { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";

export default function Navbar({ onBookClick }) {
    const [scrolled, setScrolled] = useState(false);
    const [menuOpen, setMenuOpen] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 30);
        window.addEventListener("scroll", onScroll);
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    const links = [
        { href: "#problem", label: "The Problem" },
        { href: "#how", label: "How It Works" },
        { href: "#packages", label: "Packages" },
        { href: "#about", label: "About" },
        { href: "/blog", label: "Blog", external: true },
        { href: "/track", label: "Track", external: true },
        { href: "#faq", label: "FAQ" },
    ];

    const goTo = (href) => {
        setMenuOpen(false);
        if (href.startsWith("#")) {
            // If we're not on the landing page, navigate to / with hash
            if (window.location.pathname !== "/") {
                navigate("/" + href);
                return;
            }
            const el = document.querySelector(href);
            if (el)
                el.scrollIntoView({ behavior: "smooth", block: "start" });
        } else {
            navigate(href);
        }
    };

    return (
        <nav
            data-testid="main-navbar"
            className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
                scrolled
                    ? "bg-[#FDFAF5]/85 backdrop-blur-xl border-b border-[#E6DFD3]/60 py-3"
                    : "bg-transparent py-5"
            }`}
        >
            <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between">
                <Link
                    to="/"
                    data-testid="nav-logo"
                    className="font-serif text-2xl md:text-[28px] font-medium tracking-tight text-[#0D1B1A]"
                >
                    Dhan<span className="text-[#D4944C]">Nniti</span>
                </Link>

                <div className="hidden md:flex items-center gap-8">
                    {links.map((l) => (
                        <button
                            key={l.href}
                            onClick={() => goTo(l.href)}
                            data-testid={`nav-link-${l.href.replace(/[#/]/g, "")}`}
                            className="text-sm text-[#4A5A58] hover:text-[#1A5C53] transition-colors duration-200"
                        >
                            {l.label}
                        </button>
                    ))}
                    <button
                        onClick={() => {
                            const el = document.querySelector("#packages");
                            if (el) el.scrollIntoView({ behavior: "smooth" });
                        }}
                        data-testid="nav-book-btn"
                        className="bg-[#1A5C53] text-white hover:bg-[#13423B] transition-colors duration-300 rounded-full px-5 py-2.5 text-sm font-medium"
                    >
                        Choose Package
                    </button>
                </div>

                <button
                    className="md:hidden text-[#0D1B1A]"
                    onClick={() => setMenuOpen((o) => !o)}
                    data-testid="nav-mobile-toggle"
                    aria-label="Toggle menu"
                >
                    {menuOpen ? <X size={24} /> : <Menu size={24} />}
                </button>
            </div>

            {menuOpen && (
                <div className="md:hidden bg-[#FDFAF5] border-t border-[#E6DFD3] px-6 py-4 space-y-3">
                    {links.map((l) => (
                        <button
                            key={l.href}
                            onClick={() => goTo(l.href)}
                            className="block w-full text-left py-2 text-[#4A5A58] hover:text-[#1A5C53]"
                        >
                            {l.label}
                        </button>
                    ))}
                    <button
                        onClick={() => {
                            setMenuOpen(false);
                            const el = document.querySelector("#packages");
                            if (el) el.scrollIntoView({ behavior: "smooth" });
                        }}
                        className="block w-full bg-[#1A5C53] text-white rounded-full px-5 py-3 text-sm font-medium"
                    >
                        Choose Package
                    </button>
                </div>
            )}
        </nav>
    );
}
